import React, { useState } from 'react';
import styled from 'styled-components/macro';
import Button from 'components/atoms/Button';
import Toast from 'components/molecules/Toast';
import { Error } from 'components/molecules/Field/Field.styles';
import ProgressBar from 'components/molecules/ProgressBar';
import { getCookie } from 'helpers/common';

function UploadStatementZip({ closeModal }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = e => {
    setFile(e.target.files[0]);
    setProgress({ processed: 0, total: 0 });
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) {
      Toast({ type: 'error', message: 'Please select a ZIP file' });
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${process.env.REACT_APP_COMMON_API_URL}/merchant-tool/upload-statements`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${getCookie(process.env.REACT_APP_PSP_TOKEN_COOKIE)}`,
        },
      });
      if (!res.body) throw new Error('No response body');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (value) {
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split(/\n\n/);
          parts.slice(0, -1).forEach(part => {
            if (part.startsWith('data:')) {
              const raw = part.replace(/^data:/, '').trim();
              try {
                const data = JSON.parse(raw);
                if (data.processed != null && data.total != null) {
                  setProgress({ processed: data.processed, total: data.total });
                }
                if (data.done) setUploading(false);
              } catch {
                setError('Invalid data format');
                Toast({ type: 'error', message: 'Invalid data format' });
              }
            }
          });
          buffer = parts[parts.length - 1];
        }
        if (done) break;
      }
    } catch (ex) {
      setError(ex.message);
      Toast({ type: 'error', message: ex.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      <input type="file" accept=".zip" onChange={handleFileChange} />
      {error && <Error role="alert">{error}</Error>}
      <Button type="primary" onClick={handleUpload} disabled={uploading}>
        {uploading ? 'Uploading…' : 'Upload ZIP'}
      </Button>

      {progress.total > 0 && (
        <ProgressWrapper>
          <ProgressBar level={(progress.processed / progress.total) * 100} />
          <Status>{`${progress.processed} / ${progress.total}`}</Status>
        </ProgressWrapper>
      )}
      <Button type="secondary" onClick={closeModal} disabled={uploading}>
        Close
      </Button>
    </Container>
  );
}

export default UploadStatementZip;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Status = styled.span`
  font-size: 0.9rem;
`;
